import { getTierRank } from 'loyalty/lib/tierUtils'

export type CampaignTierStanding = 'upgradable' | 'onTier' | 'above'

/**
 * Where the member's current tier sits against the campaign. The backend rejects a claim unless it
 * upgrades the member, so only `upgradable` members should claim.
 */
export function getCampaignTierStanding(
  currentTier: App.LoyaltyTier | undefined,
  campaignTiers: Array<App.LoyaltyTier>,
): CampaignTierStanding {
  if (!currentTier) return 'upgradable'
  const currentRank = getTierRank(currentTier)
  // Invite-only tiers (e.g. Infinite) sit outside the earnable order, above every campaign tier
  if (currentRank === -1) return 'above'
  if (campaignTiers.some(tier => getTierRank(tier) > currentRank)) return 'upgradable'
  return campaignTiers.includes(currentTier) ? 'onTier' : 'above'
}

/**
 * Direct (codeless) campaigns are claimed without a promo code; every other validator needs one.
 * The landing page and the site-wide auto-grant derive their flow from this, not from the Lab payload.
 */
export function isCodelessClaimCampaign(claimCampaign: App.LoyaltyClaimCampaign): boolean {
  return claimCampaign.validatorType === 'DIRECT'
}

/**
 * Codeless claims are sent automatically with no evidence and promise an instant grant, so they
 * can't support campaigns that need CS approval.
 */
export function canAutoClaimCampaign(claimCampaign: App.LoyaltyClaimCampaign): boolean {
  return isCodelessClaimCampaign(claimCampaign) && !claimCampaign.requireApproval
}

/** Campaigns with an email domain allowlist only accept customers with a verified email. */
export function requiresVerifiedEmail(claimCampaign: App.LoyaltyClaimCampaign): boolean {
  return claimCampaign.emailVerificationDomains.length > 0
}

/** The backend counts the claim as already made; other 409s (e.g. a pending approval) are not a grant. */
export function isAlreadyClaimedError(errorCode: string | undefined): boolean {
  return errorCode === 'CLAIMS_ALREADY_CLAIMED'
}

/** Mirrors the backend allowlist check; a campaign with no domains accepts any email. */
export function isEmailOnAllowedDomain(email: string | undefined, domains: Array<string> | undefined): boolean {
  if (!email) return false
  if (!domains?.length) return true
  const emailDomain = email.slice(email.lastIndexOf('@') + 1).toLowerCase()
  return domains.some(domain => domain.toLowerCase() === emailDomain)
}
